/**
 * Notification Settings Component
 * Comprehensive notification preferences including email, in-app, browser, and push notifications
 */

import { apiClient } from '../services/api-client.js';
import { pushNotifications } from '../services/push-notifications.js';
import { showToast } from './toast.js';
import { createSpinner } from './spinner.js';
import { renderIcon } from '../utils/icons.js';

/**
 * Notification preferences from backend
 */
interface NotificationPreferences {
  user_id: string;
  email_enabled: boolean;
  in_app_enabled: boolean;
  push_enabled: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Component options
 */
export interface NotificationSettingsOptions {
  parent: HTMLElement;
  className?: string;
  onUpdate?: (preferences: NotificationPreferences) => void;
}

/**
 * Create the notification settings component
 */
export function createNotificationSettings(
  options: NotificationSettingsOptions
): {
  element: HTMLElement;
  refresh: () => Promise<void>;
  destroy: () => void;
} {
  const { parent, className = '', onUpdate } = options;

  // State
  let preferences: NotificationPreferences | null = null;
  let pushStatus: { supported: boolean; permission: NotificationPermission; subscribed: boolean } = {
    supported: false,
    permission: 'default',
    subscribed: false,
  };
  let isLoading = true;

  // Create container
  const container = document.createElement('div');
  container.className = `notification-settings ${className}`.trim();
  container.style.cssText = `
    background: rgba(30, 30, 40, 0.8);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 0.75rem;
    padding: 1.5rem;
  `;

  /**
   * Load preferences from backend
   */
  async function loadPreferences(): Promise<void> {
    try {
      const response = await apiClient.get<{ data: NotificationPreferences }>('/notifications/preferences');
      preferences = response.data.data;
    } catch (error) {
      console.error('Failed to load notification preferences:', error);
      showToast('Failed to load notification preferences', 'error');
    }
  }

  /**
   * Load push notification status
   */
  async function loadPushStatus(): Promise<void> {
    try {
      pushStatus = await pushNotifications.getStatus();
    } catch (error) {
      console.error('Failed to get push status:', error);
    }
  }

  /**
   * Update preferences on backend
   */
  async function updatePreferences(updates: Partial<NotificationPreferences>): Promise<void> {
    if (!preferences) return;

    try {
      const response = await apiClient.put<{ data: NotificationPreferences }>('/notifications/preferences', {
        email_enabled: updates.email_enabled ?? preferences.email_enabled,
        in_app_enabled: updates.in_app_enabled ?? preferences.in_app_enabled,
        push_enabled: updates.push_enabled ?? preferences.push_enabled,
      });

      preferences = response.data.data;
      onUpdate?.(preferences);
      showToast('Notification preferences updated', 'success');
      render();
    } catch (error) {
      console.error('Failed to update preferences:', error);
      showToast('Failed to update preferences', 'error');
      // Reload to reset UI
      await refresh();
    }
  }

  /**
   * Handle push notification subscription
   */
  async function handlePushSubscription(): Promise<void> {
    if (!pushStatus.supported) {
      showToast('Push notifications are not supported in this browser', 'error');
      return;
    }

    try {
      if (pushStatus.subscribed) {
        // Unsubscribe
        await pushNotifications.unsubscribe();
        showToast('Unsubscribed from push notifications', 'success');
      } else {
        // Subscribe
        const success = await pushNotifications.subscribe();
        if (success) {
          showToast('Subscribed to push notifications', 'success');
        } else {
          showToast('Failed to subscribe to push notifications', 'error');
        }
      }

      // Refresh status
      await loadPushStatus();
      render();
    } catch (error: any) {
      console.error('Push subscription error:', error);
      showToast(error.message || 'Failed to manage push subscription', 'error');
    }
  }

  /**
   * Create toggle switch
   */
  function createToggle(
    id: string,
    checked: boolean,
    disabled: boolean,
    onChange: (checked: boolean) => void
  ): HTMLElement {
    const label = document.createElement('label');
    label.className = 'toggle-container';
    label.htmlFor = id;
    label.style.cssText = `
      position: relative;
      display: inline-block;
      width: 44px;
      height: 24px;
      flex-shrink: 0;
      cursor: ${disabled ? 'not-allowed' : 'pointer'};
      opacity: ${disabled ? '0.5' : '1'};
    `;

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.id = id;
    input.checked = checked;
    input.disabled = disabled;
    input.className = 'sr-only';

    const slider = document.createElement('span');
    slider.className = 'toggle-slider';
    slider.style.cssText = `
      position: absolute;
      cursor: inherit;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: ${checked ? '#6366f1' : '#4b5563'};
      transition: all 0.2s ease;
      border-radius: 24px;
    `;

    const knob = document.createElement('span');
    knob.style.cssText = `
      position: absolute;
      height: 18px;
      width: 18px;
      left: ${checked ? '23px' : '3px'};
      bottom: 3px;
      background-color: white;
      transition: all 0.2s ease;
      border-radius: 50%;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    `;

    slider.appendChild(knob);
    label.appendChild(input);
    label.appendChild(slider);

    input.addEventListener('change', () => {
      const isChecked = input.checked;
      slider.style.backgroundColor = isChecked ? '#6366f1' : '#4b5563';
      knob.style.left = isChecked ? '23px' : '3px';
      onChange(isChecked);
    });

    return label;
  }

  /**
   * Render the component
   */
  function render(): void {
    container.innerHTML = '';

    // Header
    const header = document.createElement('div');
    header.className = 'mb-6';
    header.innerHTML = `
      <h3 class="text-lg font-semibold text-foreground mb-2">Notification Settings</h3>
      <p class="text-sm text-muted-foreground">
        Configure how you receive notifications for expenditure requests
      </p>
    `;
    container.appendChild(header);

    if (isLoading) {
      const loader = document.createElement('div');
      loader.className = 'flex items-center justify-center py-8';
      loader.appendChild(createSpinner({ size: 'lg' }));
      container.appendChild(loader);
      return;
    }

    if (!preferences) {
      const error = document.createElement('div');
      error.className = 'text-center py-8 text-muted-foreground';
      error.textContent = 'Failed to load notification preferences';
      container.appendChild(error);
      return;
    }

    // Notification channels section
    const channelsSection = document.createElement('div');
    channelsSection.className = 'space-y-4';

    // Email notifications
    const emailItem = document.createElement('div');
    emailItem.className = 'flex items-center justify-between py-3 px-4 rounded-lg bg-background/50';
    emailItem.innerHTML = `
      <div class="flex items-center gap-3">
        ${renderIcon('mail', { size: 'sm', className: 'text-muted-foreground' }).outerHTML}
        <div>
          <div class="font-medium text-foreground">Email Notifications</div>
          <div class="text-sm text-muted-foreground">Receive notifications via email</div>
        </div>
      </div>
    `;
    emailItem.appendChild(
      createToggle('email-toggle', preferences.email_enabled, false, (checked) => {
        updatePreferences({ email_enabled: checked });
      })
    );
    channelsSection.appendChild(emailItem);

    // In-app notifications
    const inAppItem = document.createElement('div');
    inAppItem.className = 'flex items-center justify-between py-3 px-4 rounded-lg bg-background/50';
    inAppItem.innerHTML = `
      <div class="flex items-center gap-3">
        ${renderIcon('bell', { size: 'sm', className: 'text-muted-foreground' }).outerHTML}
        <div>
          <div class="font-medium text-foreground">In-App Notifications</div>
          <div class="text-sm text-muted-foreground">See notifications in the app</div>
        </div>
      </div>
    `;
    inAppItem.appendChild(
      createToggle('in-app-toggle', preferences.in_app_enabled, false, (checked) => {
        updatePreferences({ in_app_enabled: checked });
      })
    );
    channelsSection.appendChild(inAppItem);

    // Push notifications
    const pushItem = document.createElement('div');
    pushItem.className = 'flex flex-col gap-3 py-3 px-4 rounded-lg bg-background/50';

    const pushHeader = document.createElement('div');
    pushHeader.className = 'flex items-center justify-between';
    pushHeader.innerHTML = `
      <div class="flex items-center gap-3">
        ${renderIcon('bellRing', { size: 'sm', className: 'text-muted-foreground' }).outerHTML}
        <div>
          <div class="font-medium text-foreground">Push Notifications</div>
          <div class="text-sm text-muted-foreground">
            ${!pushStatus.supported
              ? 'Not supported in this browser'
              : pushStatus.permission === 'denied'
              ? 'Permission denied - enable in browser settings'
              : pushStatus.subscribed
              ? 'Receive browser push notifications'
              : 'Enable browser push notifications'
            }
          </div>
        </div>
      </div>
    `;

    if (pushStatus.supported && pushStatus.permission !== 'denied') {
      pushHeader.appendChild(
        createToggle('push-toggle', preferences.push_enabled, false, async (checked) => {
          if (checked && !pushStatus.subscribed) {
            // Need to subscribe first
            await handlePushSubscription();
          }
          // Always update the preference regardless of subscription status
          await updatePreferences({ push_enabled: checked });
        })
      );
    }

    pushItem.appendChild(pushHeader);

    // Push subscription status and actions
    if (pushStatus.supported) {
      const pushActions = document.createElement('div');
      pushActions.className = 'flex items-center gap-2 ml-9';

      if (pushStatus.permission === 'default') {
        const requestBtn = document.createElement('button');
        requestBtn.className = 'text-sm text-primary hover:text-primary/90 underline';
        requestBtn.textContent = 'Request Permission';
        requestBtn.addEventListener('click', async () => {
          await pushNotifications.requestPermission();
          await loadPushStatus();
          render();
        });
        pushActions.appendChild(requestBtn);
      } else if (pushStatus.permission === 'granted' && !pushStatus.subscribed) {
        const subscribeBtn = document.createElement('button');
        subscribeBtn.className = 'text-sm text-primary hover:text-primary/90 underline';
        subscribeBtn.textContent = 'Subscribe to Push';
        subscribeBtn.addEventListener('click', handlePushSubscription);
        pushActions.appendChild(subscribeBtn);
      }

      if (pushActions.children.length > 0) {
        pushItem.appendChild(pushActions);
      }
    }

    channelsSection.appendChild(pushItem);
    container.appendChild(channelsSection);
  }

  /**
   * Refresh the component
   */
  async function refresh(): Promise<void> {
    isLoading = true;
    render();

    await Promise.all([
      loadPreferences(),
      loadPushStatus(),
    ]);

    isLoading = false;
    render();
  }

  /**
   * Destroy the component
   */
  function destroy(): void {
    container.remove();
  }

  // Initialize
  parent.appendChild(container);
  refresh();

  // Initialize push notifications if not already done
  pushNotifications.initialize().catch(console.error);

  return {
    element: container,
    refresh,
    destroy,
  };
}